<script lang="ts">
import { Component, mixins, Prop, Watch } from "vue-facing-decorator";
import StepperComponent from "@/components/atoms/stepper/Stepper.vue";
import IconComponent from "@/components/atoms/Icon.vue";
import SpinnerComponent from "@/components/atoms/Spinner.vue";
import {
    AvailabilityStateMapper, controlStateService,
    panelStateService,
    PriceLevelFilterState,
    PriceLevelStateProvider,
    SeatmapAreaSwitchedEvent,
    SeatmapClientDataViewModel,
    SeatmapConfig,
    SeatmapDataFactory,
    SeatmapErrorEvent,
    SeatmapGaClickedEvent,
    SeatmapSeatClickedEvent,
    SeatmapSelectionEvent,
    SeatmapUtils, SeatmapValidator,
    SeatmapZoomEvent,
    SelectableInfo,
    TicketSelectionApplicationData,
    TicketTypeItem,
    TicketTypeSelection,
    usePromotionStore,
} from "@/domain/ticket-selection";
import {
    AnalyticsEcommerceEvent,
    AnalyticsEventName,
    AnalyticsStorage,
    ApiService,
    ClickTooltip,
    CurrencyHandler,
    FbqUtil,
    GTMTicketItemFactory,
    GTMUtils,
    IconName,
    TicketItem,
    TranslationKey,
    Url
} from "webshop-modules";
import {
    AreaClickedEvent,
    SeatInfoDTO,
    SeatmapClient,
    SeatmapReadyEvent,
    SelectionModel,
    ZoomType
} from "@eventim/seatmap-client";
import { useSeatmapStore } from "@/domain/ticket-selection/application/Seatmap.module";
import { PriceLevel, PriceListResponse, Promotion } from "webshop-api";
import { TranslationPlugin } from "@/plugins/translation.plugin";
import { useRecaptchaStore } from "@/domain/search/application/Recaptcha.store";
import { StepperState } from "@/components/atoms/stepper/stepper-state.interface";
import ButtonComponent from "@/components/atoms/button/Button.vue";
import { ButtonType } from "@/components/atoms/button/button-type.enum";
import { CheckoutData } from 'webshop-modules/google-tag-manager/entity/base/CheckoutData.interface';
import ActionButton from "@/components/atoms/action-button/ActionButton.vue";
import { BreakpointHandler, TicketSelectionDisplay } from "@/mixins";
import PriceLevelFilter from "@/components/ticket-selection/seatmap/PriceLevelFilter.vue";
import MobileControls from "@/components/ticket-selection/seatmap/controls/MobileControls.vue";
import DesktopControls from "@/components/ticket-selection/seatmap/controls/DesktopControls.vue";
import SelectionComponent from "@/components/ticket-selection/seatmap/Selection.vue";

@Component({
    components: {
        SelectionComponent,
        PriceLevelFilter,
        ActionButton,
        IconComponent,
        StepperComponent,
        ButtonComponent,
        SpinnerComponent,
        MobileControls,
        DesktopControls,
    }
})
export default class SeatmapComponent extends mixins(TicketSelectionDisplay, BreakpointHandler) {

    @Prop() public data!: TicketSelectionApplicationData;
    @Prop() public selectedFilter: PriceLevelFilterState | null | undefined;

    public countSubmit = '';
    public selectionEmptyLabel = '';
    public selectionLabel = '';
    public exitLevelLabel = '';
    public exitSelectionLabel = '';
    public filterPlaceholder = '';
    public selectedPriceLevel: PriceLevelFilterState | null | undefined;
    public loadingMessage: string | null = null;

    public readonly Translation: typeof TranslationKey = TranslationKey;
    public readonly IconName: typeof IconName = IconName;
    public readonly ButtonType: typeof ButtonType = ButtonType;

    public readonly promotionStore = usePromotionStore();
    public readonly seatmapStore = useSeatmapStore();
    public readonly panelState = panelStateService;
    public readonly controlState = controlStateService;

    public CurrencyHandler!: CurrencyHandler;

    private seatmapClient!: SeatmapClient;
    private seatmapElement!: HTMLElement;
    private isExitingLayer = false;

    protected async mounted() {
        this.CurrencyHandler = new CurrencyHandler(
            this.getCurrencyFrom(this.data) ?? '',
            this.data.currencyOptions?.currency ?? '',
            this.data.currencyOptions?.conversionRate ?? '1',
            this.data.currencyOptions?.showDoubleCurrency ?? false,
        );
        await this.seatmapStore.initializeStore(this.data, this.$apiService);
        this.initSeatmapElement();
        this.addSeatmapEventHandlers();
        this.setSeatmapContainerHeight();
        this.handleMarginAboveSeatmap();
        await this.initSeatmapClient();
    }

    protected async created() {
        this.loadingMessage = await this.$translationService
            .getTranslationFor(this.Translation.Seatmap_loading_notice);

        this.filterPlaceholder = await this.$translationService
            .getTranslationFor(this.Translation.Seatmap_price_category_filter);

        this.exitLevelLabel = await this.$translationService
            .getTranslationFor(this.Translation.Navigate_back_label);

        this.exitSelectionLabel = await this.$translationService
            .getTranslationFor(this.Translation.Seatmap_label);

        this.selectionEmptyLabel = await this.$translationService
            .getTranslationFor(this.Translation.Seatmap_no_seats_selected);

        this.selectionLabel = await this.$translationService
            .getTranslationFor(this.Translation.Seatmap_seats_selected_amount);

        this.countSubmit = await this.$translationService
            .getTranslationFor(this.Translation.Ticket_selection_ticket_count_submit);
    }

    @Watch('seatmapStore.isReady')
    private onSeatmapReadyChange() {
        this.handleSVGHeight();
    }

    @Watch('panelState.actions.selectFilter')
    private onSelectedFilterChange(priceLevelFilter: PriceLevelFilterState | null) {
        if (this.seatmapClient && (priceLevelFilter || priceLevelFilter === null)) {
            this.handleFilterSelection(priceLevelFilter);
        }
    }

    @Watch('isMobile')
    private onViewPortChange(isMobile: boolean, oldVal: boolean) {
        if (isMobile !== oldVal) {
            this.handleSeatmapUIResize();
        }
    }

    @Watch('panelState.actions.selectTicketType')
    async onSelectedTicketTypeAction(ticketTypeSelection: TicketTypeSelection, oldVal: TicketTypeSelection | null) {
        await this.handleTicketTypeSelection(ticketTypeSelection.active, ticketTypeSelection.selectionItem);
    }

    @Watch('panelState.actions.removeSelection')
    async onRemoveSelectionAction(selection: SelectableInfo, oldVal: SelectableInfo | null) {
        await this.handleRemoveSelectionAction(selection);
    }

    @Watch('panelState.actions.submitSelection')
    async onSubmitAction(val) {
        await this.handleSubmitSelectionAction();
    }

    @Watch('promotionStore.active')
    async onPromotionChange(val: Promotion | null, oldVal: Promotion | null) {
        if (val === oldVal || !this.seatmapElement || !this.seatmapClient) {
            return;
        }

        await this.seatmapStore.resetTokenRefetchCounter();
        this.selectedPriceLevel = null;
        this.destroySeatmapClient();
        this.initSeatmapClient();
        await this.seatmapStore.initializeStore(this.data, this.$apiService);
        this.handleSeatmapUIResize();

        if (
            SeatmapUtils.isAvailabilityLockedBehindPromotions(this.priceLevels, this.promotionStore.active)
        ) {
            this.panelState.activatePromotionPanel();
        }
    }

    @Watch('displayStateStore.isSeatmapDisplayed')
    async initializeSeatmap(isDisplayed: boolean, oldVal: boolean) {
        if (!isDisplayed) {
            return;
        }

        await this.seatmapStore.validateSelectionState(0);
        await this.seatmapStore.resetTokenRefetchCounter();
        this.destroySeatmapClient();
        this.initSeatmapClient();

        await this.seatmapStore.initializeStore(this.data, this.$apiService);
        this.handleSeatmapUIResize();
        this.toggleAdditionalInformationVisibilityTo(!isDisplayed || !this.isMobile);

        if (
            SeatmapUtils.isAvailabilityLockedBehindPromotions(this.priceLevels, this.promotionStore.active)
            && this.seatmapStore.hasTicketsAvailable
        ) {
            this.panelState.activatePromotionPanel();
        }
    }

    @Watch('seatmapStore.getToken')
    async reinitializeSeatmap() {
        if (this.seatmapStore.tokenRequestsCounter === 2 && !this.seatmapStore.hasErrorOnUserInteraction) {
            this.destroySeatmapClient();
            await this.seatmapStore.resetStore();
            this.initSeatmapClient();
        }

        if (this.seatmapStore.tokenRequestsCounter > 2) {
            await this.seatmapStore.toggleSeatmapError(true);
        }
    }

    protected handleBreakpointChange(): void {
        this.isMobile = this.mobileBreakpoint.matches;
        this.handleMarginAboveSeatmap();

        if (this.displayModeState.seatmap.isSeatmapVisible) {
            this.toggleAdditionalInformationVisibilityTo(!this.isMobile);
        }
    }

    public get priceList(): PriceListResponse | null {
        return this.data.priceList;
    }

    public get actionButtonLabel(): string {
        return this.seatmapStore.getStructuredSelection.length === 0
            ? this.selectionEmptyLabel
            : TranslationPlugin.decorateWithParams(this.selectionLabel, {
                key: this.Translation.Seatmap_seats_selected_amount,
                keyValues: JSON.stringify({
                    'selected-seats-amount': this.seatmapClient ?
                        this.totalTicketsSelected()
                        : 0
                }),
                identifierStart: '{',
                identifierEnd: '}'
            });
    }

    public get shouldDisplayNotice(): boolean {
        return (this.seatmapStore.hasTicketNumberValidationNotice || this.hasAvailabilityLockedBehindPromotions)
            && !this.seatmapStore.hasGenericError;
    }

    public get ticketsPrice(): number {
        if (!this.seatmapStore) {
            return 0;
        }

        let total = 0;

        this.seatmapStore.getStructuredSelection.forEach(pricelevel => {
            total += pricelevel.selectionInfo.ticketTypes.reduce((sel, cur) => {
                if (!sel) {
                    return cur;
                }

                if (sel.active) {
                    return sel;
                }

                if (cur.active) {
                    return cur;
                }

                return sel.number < cur.number ? sel : cur;
            }).price;
        });

        return total;
    }

    public totalTicketsSelected() {
        return this.seatmapClient ?
            this.seatmapClient.getCurrentSelection().reduce((acc, selection) => acc += selection.amount, 0)
            : 0;
    }

    public get totalSelectionValueFormatter() {
        return this.isMobile
            ? `${ this.CurrencyHandler.displayPrimaryCurrency(this.ticketsPrice) }`
            : TranslationPlugin.decorateWithParams(this.countSubmit, {
                key: this.Translation.Ticket_selection_ticket_count_submit,
                keyValues: JSON.stringify({
                    'amount': this.totalTicketsSelected(),
                    'price': this.CurrencyHandler.displayPrimaryCurrency(this.ticketsPrice)
                }),
                identifierStart: '{',
                identifierEnd: '}'
            });
    }

    public get hasAvailabilityLockedBehindPromotions(): boolean {
        return SeatmapUtils.isAvailabilityLockedBehindPromotions(this.priceLevels, this.promotionStore.active);
    }

    public async handleTicketTypeSelection(active: TicketTypeItem, selectionItem: SelectableInfo) {
        this.seatmapStore.updateStructuredSelection(selectionItem, active);

        await this.seatmapStore.validateSelectionState(this.seatmapClient.getCurrentSelection().length);
    }

    public async handleExitLayerClick() {
        this.isExitingLayer = true;
        this.seatmapClient.showArea(this.seatmapStore.getAreaId);
    }

    public async handleSubmitSelectionAction() {
        const recaptchaStore = useRecaptchaStore();
        const recaptchaRequired = recaptchaStore.isRequired();
        if (!this.seatmapStore.validation.isValid) {
            return;
        }

        const seatMap = new Map<string, number>();
        const standMap = new Map<string, number>();
        const actionPath = this.data.configuration.actionPath;
        const actionParams = Object.entries(this.data.configuration)
            .filter(([key]) => key !== 'actionPath')
            .filter(([key]) => key !== 'reCaptcha')
            .filter(([key]) => key !== 'cartCurrency')
            .filter(([key]) => key !== 'poolCurrency')
            .filter(([key]) => key !== 'currency')
            .filter(([key]) => key !== 'staticPath')
            .reduce((pre: string | null, cur) => {
                const reducedCur = cur.reduce((prev, curr) => `${ prev }=${ curr }`);
                return !pre ? reducedCur : `${ pre }&${ reducedCur }`;
            }, null);
        let submitParams = '';

        if (!SeatmapValidator.isCurrencyValidFor(this.seatmapStore.getStructuredSelection)) {
            this.seatmapStore.setMismatchedCurrencyTo(true);
            throw new Error('Mismatched pricelist currencies!');
        }

        const currency = this.seatmapStore.getStructuredSelection[0].selectionInfo.currency;

        this.seatmapStore.getStructuredSelection.forEach((pricelevel, index) => {
            const priceId = pricelevel.selectionInfo.ticketTypes.find(ticketType => ticketType.active)!.priceId;
            const key = `${ pricelevel.selectionInfo.id }_${ priceId }_${ pricelevel.selectionInfo.priceCategoryId }`;
            const map = pricelevel.selectionInfo.isGeneralAdmission ? standMap : seatMap;

            if (map.has(key)) {
                map.set(key, map.get(key)! + 1)
            } else {
                map.set(key, 1);
            }
        })

        // Set seats params
        seatMap.forEach((value, key) => {
            submitParams = submitParams.length === 0
                ? '?' + SeatmapUtils.getSeatParamsFor(key, value)
                : submitParams + '&' + SeatmapUtils.getSeatParamsFor(key, value);
        });

        // Set GA params
        standMap.forEach((value, key) => {
            submitParams = submitParams.length === 0
                ? '?' + SeatmapUtils.getStandParamsFor(key, value)
                : submitParams + '&' + SeatmapUtils.getStandParamsFor(key, value);
        });

        let url = location.origin + actionPath + `${ submitParams }&${ actionParams }&currency=${ currency }&seatmap=1`;
        const promotion = this.promotionStore.activePromotion;
        if (promotion) {
            url += `&promotionId=${ encodeURIComponent(promotion.id!) }`;
        }

        if (promotion && promotion.isCodeRequired && this.promotionStore.promotionCode) {
            url += `&promotionCode=${ encodeURIComponent(this.promotionStore.promotionCode) }`;
        }

        const eventId = Number.parseInt(new Url(url).getSearchParam('eventId')!);
        const ticketTypeData = PriceLevelStateProvider.getAnalyticsCartTicketsFromSeatmap(this.seatmapStore.getStructuredSelection);
        const ticketItems: TicketItem[] = ticketTypeData.ticketTypes.map(ticketType => GTMTicketItemFactory.fromTicketSelection(
            ticketType,
            'seatmap',
            this.data.eventInformation.eventName,
            GTMUtils.parseItemIdFromCurrentUrl() ?? '-',
        ));
        const ecommerceEvent: AnalyticsEcommerceEvent<CheckoutData<TicketItem>> = {
            event: AnalyticsEventName.AddToCart,
            ecommerce: {
                currency: this.getCurrencyFrom(this.data),
                value: this.ticketsPrice,
                items: ticketItems,
            }
        };

        if (recaptchaRequired) {
            recaptchaStore.queueSubmit(url, ecommerceEvent);
            recaptchaStore.instance().execute();

            return;
        }

        const analyticsStore = AnalyticsStorage.getInstance();

        FbqUtil.fbqEvent('AddToCart', [`${ eventId }`], 'product');
        GTMUtils.pushGA4EcommerceToDataLayer(ecommerceEvent);

        if (analyticsStore) {
            analyticsStore.updateLocalStore({currency: this.getCurrencyFrom(this.data)});
            analyticsStore.pushCartItems(ticketItems);
        }

        window.location.assign(url);
    }

    public handleZoomControlChange(state: StepperState) {
        const currentLevel = this.seatmapStore.zoom.amount;

        if (state.amount > currentLevel && state.canIncrease) {
            this.seatmapClient.zoom(ZoomType.IN_OUT, 2);
        }

        if (state.amount < currentLevel && state.canDecrease) {
            this.seatmapClient.zoom(ZoomType.IN_OUT, 0.2);
        }
    }

    public handleFilterSelection(input: PriceLevelFilterState | null): void {
        if (!input && this.data.seatmapConfig) {
            this.seatmapClient.filter({
                priceCategories: SeatmapDataFactory.getPriceCategoryIdsForInitialFilterFrom(this.priceLevels, this.promotionStore.active),
            });
            this.selectedPriceLevel = null;

            return;
        }

        this.selectedPriceLevel = input;

        if (input) {
            this.seatmapClient.filter({priceCategories: [input.id]});
        }
    }

    public shouldDisplayUnavailabilityOverlay() {
        return !this.seatmapStore.hasTicketsAvailable
            || !this.displayStateStore.getPriceList?.priceLevels?.length;
    }

    public async handleRemoveSelectionAction(item: SelectableInfo) {
        const selection = this.seatmapClient.getCurrentSelection().find(selection => selection.selectable.id === item.id);

        if (selection) {
            this.seatmapClient.deselect([{selectable: selection.selectable, amount: 1}]);
        }
    }

    private get seatmapConfig(): SeatmapConfig {
        const config: SeatmapConfig = {...this.data.seatmapConfig as SeatmapConfig};
        config.promotionId = this.promotionStore.active ? this.promotionStore.active.id! : 0;

        return config;
    }

    private get priceLevels(): PriceLevel[] {
        if (this.priceList) {
            return this.priceList.priceLevels ?? [];
        }

        return [];
    }

    private async getSeatmapInitOptions() {
        if (!this.seatmapConfig) {
            return;
        }

        this.seatmapClient.init({
            apiOptions: {
                id: SeatmapDataFactory.setupAPIConnectorFrom(this.seatmapConfig),
                baseUrl: this.seatmapConfig.seatMapServiceUrl,
                params: (() => SeatmapDataFactory.setupAdditionalParamsFrom(this.seatmapConfig, this.seatmapStore.getToken)),
            },
            filtered: {priceCategories: this.selectedPriceLevel ?
                [this.selectedPriceLevel.id]
                : SeatmapDataFactory.getPriceCategoryIdsForInitialFilterFrom(this.priceLevels, this.promotionStore.active)
            },
            render: SeatmapDataFactory.setupRenderOptionsFor(this.seatmapClient),
        });
    }

    private initSeatmapClient() {
        this.seatmapClient = new SeatmapClient('#map');

        if (this.seatmapClient) {
            this.getSeatmapInitOptions();
        }
    }

    private hasExpiredToken(): boolean {
        return this.seatmapStore.tokenData.expireDate <= Date.now();
    }

    private handleMarginAboveSeatmap() {
        const container = <HTMLElement>document.querySelector('[data-js=seatmap-container]');
        const eventPresentation = <HTMLElement>document.querySelector('#event-presentation-component');

        if (!container || !eventPresentation) {
            return;
        }

        container.style.marginTop = this.isMobile ?
            eventPresentation.getClientRects().item(0)!.height + 'px'
            : 0 + 'px';
    }

    private initSeatmapElement() {
        this.seatmapElement = this.$refs['map-container'] as HTMLElement;
    }

    private destroySeatmapClient(): void {
        if (this.seatmapClient) {
            this.seatmapClient.destroy();
        }
    }

    private async handleTokenRefetchAndSelectionEventRetrigger(selection: SelectionModel, api: ApiService) {
        await this.seatmapStore.toggleUserInteractionError(true);
        await this.seatmapStore.updateTDLToken(api);

        if (selection.added.length && !this.seatmapStore.hasGenericError) {
            this.seatmapClient.select(selection.added);
        }

        if (selection.removed.length && !this.seatmapStore.hasGenericError) {
            this.seatmapClient.deselect(selection.removed)
        }
    }

    private async handleTokenRefetchAndAreaEventRetrigger(areaEvent: AreaClickedEvent, api: ApiService) {
        await this.seatmapStore.toggleUserInteractionError(true);
        await this.seatmapStore.updateTDLToken(api);

        if (!this.seatmapStore.hasGenericError) {
            this.seatmapClient.getArea(areaEvent.areaId);
        }
    }

    private async displayUnavailabilityTooltipWith(text: string, clickEvent: Event) {
        const tooltip = new ClickTooltip({
            text,
            fadeAfter: 3000,
            domEvent: clickEvent,
            parentElement: this.seatmapElement,
            cssClass: 'o-seatmapSelection__tooltip',
            onCursor: true,
        });

        tooltip.append();
    }

    private isSeatInformationAvailableFor(seat: SeatInfoDTO | undefined): seat is SeatInfoDTO {
        return !!(seat && seat.seatNumber && seat.row);
    }

    private handleSeatmapUIResize() {
        this.setSeatmapContainerHeight();
        this.handleMarginAboveSeatmap();
        this.handleSVGHeight();
    }

    private handleSVGHeight() {
        if (!this.seatmapElement) {
            return;
        }

        const svg = this.seatmapElement.querySelector('svg');
        const seatmapSelection = document.querySelector('#seatmap-selection') as HTMLElement;

        if (svg && seatmapSelection && seatmapSelection.getClientRects().item(0)) {
            const height: number = seatmapSelection.getClientRects().item(0)!.height ?? 0;
            svg.setAttribute('height', `${ height }px`);
        }
    }

    public setSeatmapContainerHeight() {
        const seatmapSelection = document.querySelector('#seatmap-selection') as HTMLElement;
        const excessHeightElements: (HTMLElement | null)[] = [
            this.isMobile ? null : document.querySelector('#mode-selection') as HTMLElement,
            this.isMobile ? null : document.querySelector('#mainHeader') as HTMLElement,
            this.isMobile ? null : document.querySelector('[data-js=panel-header]'),
            document.querySelector('#event-presentation-component') as HTMLElement,
        ];

        const excessHeight = excessHeightElements.reduce((acc, el) => {
            if (el && el.getClientRects().item(0)) {
                return acc + el.getClientRects().item(0)!.height;
            }

            return acc;
        }, 0);

        const seatmapHeight = window.innerHeight - excessHeight - 30;
        seatmapSelection.style.height = `${ this.isMobile ? seatmapHeight : Math.max(seatmapHeight, 500) }px`;
    }


    private addSeatmapEventHandlers() {
        if (!this.seatmapElement) {
            return;
        }

        this.seatmapElement.addEventListener('seatmap:ready', (e) => {
            const seatmapReadyEvent = e as CustomEvent<SeatmapReadyEvent>;

            this.seatmapStore.handleSeatmapReady(
                this.seatmapClient.getPriceCategories(),
                AvailabilityStateMapper.fromSeatmap(seatmapReadyEvent.detail.seatmap),
            );
        });

        this.seatmapElement.addEventListener('seatmap:error', async (e) => {
            const error = e as SeatmapErrorEvent;

            await this.seatmapStore.handleSeatmapError(error, this.$apiService);
        });

        this.seatmapElement.addEventListener('seatmap:selection-before', async (e) => {
            if (this.hasExpiredToken()) {
                e.preventDefault();
                const seatmapSelectionEvent = e as SeatmapSelectionEvent<SelectionModel>;

                await this.handleTokenRefetchAndSelectionEventRetrigger(seatmapSelectionEvent.detail.selection, this.$apiService);
            }
        });

        this.seatmapElement.addEventListener('seatmap:seat-clicked', async (e) => {
            const seatClickedEvent = e as CustomEvent<SeatmapSeatClickedEvent>;

            if (seatClickedEvent.detail.selectable.available && !seatClickedEvent.detail.selectable.excluded) {
                return;
            }

            const seatInfo: SeatInfoDTO | undefined = await SeatmapUtils.getAdditionalInfoFor(
                seatClickedEvent.detail.selectable,
                new SeatmapClientDataViewModel(this.seatmapClient),
            );

            let seatText = `${ await this.$translationService.getTranslationFor(this.Translation.Seatmap_seat_unavailable) }`;

            if (this.isSeatInformationAvailableFor(seatInfo)) {
                seatText =
                    `${ await this.$translationService.getTranslationFor(this.Translation.Row_label) }: ${ seatInfo.row } <br>` +
                    `${ await this.$translationService.getTranslationFor(this.Translation.Seat_label) }: ${ seatInfo.seatNumber }<br>` +
                    seatText;
            }

            await this.displayUnavailabilityTooltipWith(seatText, e);
        });

        this.seatmapElement.addEventListener('seatmap:ga-clicked', async (e) => {
            const seatClickedEvent = e as CustomEvent<SeatmapGaClickedEvent>;

            if (seatClickedEvent.detail.selectable.available) {
                return;
            }

            const text = await this.$translationService.getTranslationFor(TranslationKey.Seatmap_general_admission_unavailable);
            await this.displayUnavailabilityTooltipWith(text, e);
        });


        this.seatmapElement.addEventListener('seatmap:selection-changed', async (event) => {
            await this.seatmapStore.selectSeatmapItems(new SeatmapClientDataViewModel(this.seatmapClient));

            if (this.seatmapStore.hasErrorOnUserInteraction) {
                await this.seatmapStore.toggleUserInteractionError(false);
            }
        });

        this.seatmapElement.addEventListener('seatmap:zoomed', (e) => {
            const event = e as SeatmapZoomEvent;

            this.seatmapStore.handleSeatmapZoom(event);
        });

        this.seatmapElement.addEventListener('seatmap:area-switched', async (e) => {
            const areaSwitchedEvent = e as SeatmapAreaSwitchedEvent;
            if (this.hasExpiredToken()) {
                e.preventDefault();
                await this.handleTokenRefetchAndAreaEventRetrigger(areaSwitchedEvent.detail, this.$apiService);
            }

            this.seatmapStore.changeLayerLevel({e: areaSwitchedEvent, toPrevious: this.isExitingLayer});
            this.controlState.switchExitLayerButtonDisplayTo(!!this.seatmapClient.getArea().id);
            this.isExitingLayer = false;

            if (this.seatmapClient.getArea().id) {
                this.seatmapClient.zoom(ZoomType.SEAT_LEVEL);
            }

            if (this.seatmapStore.hasErrorOnUserInteraction) {
                await this.seatmapStore.toggleUserInteractionError(false);
            }
        });
    }

}
</script>

<template>
    <div id="seatmap-selection" data-js="seatmap-container" class="o-seatmapSelection">
        <div v-if="!seatmapStore.hasGenericError && !seatmapStore.isReady" class="o-seatmapSelection__loaderContainer">
            <spinner-component :dataID="'map-spinner-component'"
                :label="loadingMessage"
                class="o-seatmapSelection__loader"/>
        </div>

        <div v-if="seatmapStore.hasGenericError"
             v-bind:class="{ 'is-absolute-positioned' : isMobile, 'is-relative-positioned': !isMobile }"
             class="o-seatmapSelection__errorBlock clearfix">
            <h3 v-translate="{ key: Translation.Seatmap_error }">Seatmap error</h3>
            <p v-translate="{ key: Translation.Seatmap_runtime_error }">Something went awry.</p>
        </div>

        <div v-else-if="shouldDisplayUnavailabilityOverlay()" class="o-seatmapSelection__overlay" >
            <h2 v-translate="{ key: Translation.Seatmap_tickets_unavailable }">Tickets are currently unavailable</h2>
        </div>

        <transition name="fade">
            <div id="map-container"
                v-bind:class="{'-hidden' : !seatmapStore.isReady}"
                class="o-seatmapSelection__container">

                <div v-if="!seatmapStore.hasGenericError"
                     v-show="seatmapStore.errorOnUserInteraction || !seatmapStore.isReady"
                     class="o-seatmapSelection__loaderContainer">
                    <spinner-component :dataID="'map-spinner-component'"
                        :label="seatmapStore.hasErrorOnUserInteraction ? '' : loadingMessage"
                        class="o-seatmapSelection__loader"/>
                </div>

                <div v-show="!seatmapStore.hasGenericError" class="o-seatmapSelection__map">
                    <div ref="map-container" id="map"></div>
                    <MobileControls v-if="isMobile"
                        :action-button-label="actionButtonLabel"
                        :is-pricelevel-selected="!!selectedPriceLevel"
                        v-on:layer-exit="handleExitLayerClick">
                    </MobileControls>
                    <DesktopControls v-else
                        :data="data"
                        :is-pricelevel-selected="!!selectedPriceLevel"
                        v-on:zoom-level-change="handleZoomControlChange"
                        v-on:layer-exit="handleExitLayerClick">
                    </DesktopControls>
                </div>

                <div v-if="shouldDisplayNotice"
                     v-bind:class="{
                        '-error': seatmapStore.hasTicketNumberValidationNotice,
                        '-warning': hasAvailabilityLockedBehindPromotions,
                        '-bottomMargin': isMobile,
                    }"
                    class="m-blockNotice o-seatmapSelection__noticeContainer -small">

                    <div class="m-blockNotice__content">
                        <p v-if="seatmapStore.hasInvalidSize"
                           v-translate="{
                            key: Translation.Seatmap_max_selection_error,
                            keyValues: JSON.stringify({ 'placeholder-max-selection': seatmapStore.maxTickets }),
                                        identifierStart: '{',
                            identifierEnd: '}',
                        }">Selection maximum exceeded notice</p>

                        <p v-if="seatmapStore.hasInvalidCartSize"
                           v-translate="{
                            key: Translation.Seatmap_max_cart_error,
                            keyValues: JSON.stringify({ 'placeholder-max-cart': seatmapStore.maxCartRestriction() }),
                            identifierStart: '{',
                            identifierEnd: '}',
                        }">Cart maximum exceeded notice</p>

                        <p v-if="hasAvailabilityLockedBehindPromotions && !shouldDisplayUnavailabilityOverlay()"
                           v-translate="{ key: Translation.Seatmap_select_promotion }">Please select promotion</p>
                    </div>
                </div>


                <div v-if="!isMobile && !seatmapStore.hasGenericError" class="o-seatmapSelection__sidebar">
                    <selection-component :data="data"></selection-component>
                </div>
            </div>
        </transition>
    </div>
</template>

<style scoped>
    .o-seatmapSelection__container {
        transition: opacity 300ms ease-in-out;
    }

    .o-seatmapSelection__container.-hidden {
        opacity: 0;
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity 300ms ease-in-out;
    }

    .fade-enter, .fade-leave-to {
        opacity: 0;
    }
</style>
